import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputMessage,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { updateRewardDescription } from "redux/actions";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

interface Props {
  voucherId: string;
}

const initialValues = {
  title: "",
  termsAndCondition: "",
  purcaseAndUse: "",
  titleEn: "",
  termsAndConditionEn: "",
  purcaseAndUseEn: "",
};

const VoucherDescriptionFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  voucherId,
}) => {
  const isAddMode = !voucherId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { vouchers, isFormLoading } = useSelector(
    (state: any) => state.voucherReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    const {
      title,
      termsAndCondition,
      purcaseAndUse,
      titleEn,
      termsAndConditionEn,
      purcaseAndUseEn,
    } = values;
    const catalogId = voucherId;

    dispatch(
      updateRewardDescription(
        {
          catalogId,
          title,
          termsAndCondition,
          purcaseAndUse,
          titleEn,
          termsAndConditionEn,
          purcaseAndUseEn,
        },
        handleCloseModal
      ) as any
    );
  };

  useEffect(() => {
    if (!isAddMode) {
      const voucher = vouchers.find(
        (voucher: any) => voucher.id === parseInt(voucherId)
      );

      const description = voucher ? voucher.description : null;

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "title",
          description && description.title ? description.title : ""
        );
        formikRef.current.setFieldValue(
          "termsAndCondition",
          description && description.terms_and_condition
            ? description.terms_and_condition
            : ""
        );
        formikRef.current.setFieldValue(
          "purcaseAndUse",
          description && description.purcase_and_use
            ? description.purcase_and_use
            : ""
        );
        formikRef.current.setFieldValue(
          "titleEn",
          description && description.title_en ? description.title_en : ""
        );
        formikRef.current.setFieldValue(
          "termsAndConditionEn",
          description && description.terms_and_condition_en
            ? description.terms_and_condition_en
            : ""
        );
        formikRef.current.setFieldValue(
          "purcaseAndUseEn",
          description && description.purcase_and_use_en
            ? description.purcase_and_use_en
            : ""
        );
      }
    }
  }, [isAddMode, vouchers, voucherId, appUrl]);

  const validationSchema = yup.object({
    title: yup.string().required("Title is required field"),
    termsAndCondition: yup
      .string()
      .required("Terms & Condition is required field"),
    purcaseAndUse: yup
      .string()
      .required("How To Purcase & Use is required field"),
    titleEn: yup.string().required("Title - English is required field"),
    termsAndConditionEn: yup
      .string()
      .required("Terms & Condition - English is required field"),
    purcaseAndUseEn: yup
      .string()
      .required("How To Purcase & Use - English is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxWidth={"800px"}
      height={"1000px"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>{"Update Voucher Description"}</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="title">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="title">Title</FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("title", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="termsAndCondition">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="termsAndCondition">
                          Terms & Condition
                        </FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("termsAndCondition", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="purcaseAndUse">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="purcaseAndUse">
                          How To Purcase & Use
                        </FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("purcaseAndUse", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="titleEn">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="titleEn">Title - English</FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("titleEn", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="termsAndConditionEn">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="termsAndConditionEn">
                          Terms & Condition - English
                        </FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("termsAndConditionEn", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="purcaseAndUseEn">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="purcaseAndUseEn">
                          How To Purcase & Use - English
                        </FormLabel>
                        <Box>
                          <CKEditor
                            editor={ClassicEditor}
                            data={field.value}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setFieldValue("purcaseAndUseEn", data);
                            }}
                          />
                        </Box>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Button
                  onClick={() => handleCloseModal()}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isFormLoading}>
                  Submit
                </Button>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default VoucherDescriptionFormModal;
